import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus, Trash2, ChevronDown, Sparkles } from 'lucide-react'
import { strategiesApi } from '../lib/api'
import { cn } from '../lib/utils'

interface StrategyRule {
  condition: string
  value: string
  action: string
  positionSize?: number
}

interface Strategy {
  id: string
  name: string
  description?: string
  rules: StrategyRule[]
  isActive: boolean
}

interface StrategyBuilderModalProps {
  strategy?: Strategy | null
  onClose: () => void
}

const conditions = [
  { value: 'PRICE_ABOVE', label: 'Price is above', inputType: 'price', placeholder: '0.65' },
  { value: 'PRICE_BELOW', label: 'Price is below', inputType: 'price', placeholder: '0.35' },
  { value: 'PRICE_CHANGE_PCT_ABOVE', label: 'Price change % above', inputType: 'percent', placeholder: '5' },
  { value: 'PRICE_CHANGE_PCT_BELOW', label: 'Price change % below', inputType: 'percent', placeholder: '-5' },
  { value: 'VOLUME_ABOVE', label: '24h volume above', inputType: 'number', placeholder: '10000' },
  { value: 'LIQUIDITY_ABOVE', label: 'Liquidity above', inputType: 'number', placeholder: '50000' },
  { value: 'SPREAD_BELOW', label: 'Spread below', inputType: 'price', placeholder: '0.02' },
  { value: 'WHALE_BUYING', label: 'Whales buying (count)', inputType: 'number', placeholder: '3' },
  { value: 'WHALE_SELLING', label: 'Whales selling (count)', inputType: 'number', placeholder: '3' },
  { value: 'AI_CONFIDENCE_ABOVE', label: 'AI confidence above', inputType: 'percent', placeholder: '0.8' },
  { value: 'TIME_REMAINING_BELOW', label: 'Time remaining below (hours)', inputType: 'number', placeholder: '24' },
  { value: 'MARKET_CATEGORY_IS', label: 'Market category is', inputType: 'select', options: ['crypto', 'sports', 'politics', 'entertainment', 'science', 'other'] },
  { value: 'MARKET_STATUS_IS', label: 'Market status is', inputType: 'select', options: ['active', 'closing', 'resolved'] },
]

const actions = [
  { value: 'BUY_YES', label: 'Buy YES shares', color: 'green' },
  { value: 'BUY_NO', label: 'Buy NO shares', color: 'red' },
  { value: 'SELL_YES', label: 'Sell YES shares', color: 'orange' },
  { value: 'SELL_NO', label: 'Sell NO shares', color: 'orange' },
  { value: 'ALERT', label: 'Send alert only', color: 'blue' },
]

export function StrategyBuilderModal({ strategy, onClose }: StrategyBuilderModalProps) {
  const queryClient = useQueryClient()
  const isEditing = strategy?.id && strategy.id !== 'new'

  const [name, setName] = useState(strategy?.name || '')
  const [description, setDescription] = useState(strategy?.description || '')
  const [rules, setRules] = useState<StrategyRule[]>(
    strategy?.rules?.length > 0 
      ? strategy.rules 
      : [{ condition: 'PRICE_ABOVE', value: '', action: 'BUY_YES', positionSize: 100 }]
  )
  const [isActive, setIsActive] = useState(strategy?.isActive ?? false)

  const createMutation = useMutation({
    mutationFn: (data: any) => strategiesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] })
      onClose()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: any) => 
      strategiesApi.update(strategy!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] })
      onClose()
    },
  })

  const addRule = useCallback(() => {
    setRules(prev => [...prev, { 
      condition: 'PRICE_ABOVE', 
      value: '', 
      action: 'BUY_YES',
      positionSize: 100 
    }])
  }, [])

  const removeRule = useCallback((index: number) => {
    setRules(prev => prev.filter((_, i) => i !== index))
  }, [])

  const updateRule = useCallback((index: number, field: keyof StrategyRule, value: any) => {
    setRules(prev => prev.map((rule, i) =>
      i === index ? { ...rule, [field]: value } : rule
    ))
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const payload = {
      name,
      description,
      rules: rules.map(r => ({
        ...r,
        positionSize: r.positionSize || 100,
      })),
      isActive,
    }

    if (isEditing) {
      updateMutation.mutate(payload)
    } else {
      createMutation.mutate(payload)
    }
  }

  const isValid = name.trim() && rules.length > 0 && rules.every(r => r.value !== '')

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? 'Edit Strategy' : 'Create Strategy'}
              </h2>
              <p className="text-sm text-gray-500">Build automated trading rules</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Strategy Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Strategy Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Momentum Breakout"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe what this strategy does..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Rules Builder */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-gray-700">
                Trading Rules *
              </label>
              <button
                type="button"
                onClick={addRule}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Rule
              </button>
            </div>

            <div className="space-y-3">
              {rules.map((rule, index) => {
                const condition = conditions.find(c => c.value === rule.condition)
                
                return (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-3">
                        {/* Condition */}
                        <div className="md:col-span-4">
                          <label className="block text-xs text-gray-500 mb-1">When</label>
                          <div className="relative">
                            <select
                              value={rule.condition}
                              onChange={(e) => {
                                const newCondition = conditions.find(c => c.value === e.target.value)
                                updateRule(index, 'condition', e.target.value)
                                // Reset value when condition changes
                                if (newCondition?.inputType === 'select') {
                                  updateRule(index, 'value', newCondition.options?.[0] || '')
                                } else {
                                  updateRule(index, 'value', '')
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none bg-white"
                            >
                              {conditions.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Value */}
                        <div className="md:col-span-3">
                          <label className="block text-xs text-gray-500 mb-1">Value</label>
                          {condition?.inputType === 'select' ? (
                            <select
                              value={rule.value}
                              onChange={(e) => updateRule(index, 'value', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              {condition.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="relative">
                              <input
                                type="text"
                                value={rule.value}
                                onChange={(e) => updateRule(index, 'value', e.target.value)}
                                placeholder={condition?.placeholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                              {condition?.inputType === 'percent' && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action */}
                        <div className="md:col-span-3">
                          <label className="block text-xs text-gray-500 mb-1">Then</label>
                          <div className="relative">
                            <select
                              value={rule.action}
                              onChange={(e) => updateRule(index, 'action', e.target.value)}
                              className={cn(
                                'w-full px-3 py-2 border rounded-lg text-sm appearance-none bg-white',
                                rule.action.includes('BUY_YES') && 'border-green-300 text-green-700',
                                rule.action.includes('BUY_NO') && 'border-red-300 text-red-700',
                                rule.action.includes('SELL') && 'border-orange-300 text-orange-700',
                                rule.action === 'ALERT' && 'border-blue-300 text-blue-700'
                              )}
                            >
                              {actions.map(a => (
                                <option key={a.value} value={a.value}>{a.label}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Position Size */}
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Size ($)</label>
                          <input
                            type="number"
                            value={rule.positionSize}
                            onChange={(e) => updateRule(index, 'positionSize', parseFloat(e.target.value) || 0)}
                            min="0.01"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => removeRule(index)}
                        disabled={rules.length === 1}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Activate Option */}
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <input
              type="checkbox"
              id="activate"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded border-gray-300"
            />
            <label htmlFor="activate" className="text-sm font-medium text-gray-700 cursor-pointer">
              Activate strategy immediately
            </label>
          </div>

          {/* Error */}
          {(createMutation.error || updateMutation.error) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {(createMutation.error as any)?.response?.data?.error || 
               (updateMutation.error as any)?.response?.data?.error || 
               'Failed to save strategy'}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || createMutation.isPending || updateMutation.isPending}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isEditing
                ? 'Update Strategy'
                : 'Create Strategy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
